import { useEffect, useState, useCallback } from 'react';
import dateFormat from 'dateformat';

import '../../Assets/Pages/referralStats.css';

import { fetchApi } from '../../Network/NetworkService';
import { notif } from '../Ui/Notification';
import { exportAsExcel } from '../../Services/exportAsExcel';

const ReferralStats = () => {
    const [stats, setStats] = useState(null);
    const [monthly, setMonthly] = useState([]);
    const [topReferrers, setTopReferrers] = useState([]);
    const [recentUsers, setRecentUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [sortKey, setSortKey] = useState('referralCount');
    const [sortAsc, setSortAsc] = useState(false);

    const defaultFilters = { startDate: '', endDate: '', specialty: '', doctorId: '', recurrence: '' };
    const [filters, setFilters] = useState(defaultFilters);
    const [filterOptions, setFilterOptions] = useState({ specialties: [], doctors: [] });

    const updateFilter = function(key, value) {
        setFilters(function(prev) { return Object.assign({}, prev, { [key]: value }); });
    };

    // Load filter options once
    useEffect(function() {
        fetchApi('/referral-analytics/filters')
            .then(function(res) {
                if (res && res.success) {
                    setFilterOptions({
                        specialties: res.specialties || [],
                        doctors: res.doctors || []
                    });
                }
            })
            .catch(function() {});
    }, []);

    const fetchStats = useCallback(() => {
        setLoading(true);
        const params = new URLSearchParams();
        if (filters.startDate && filters.endDate) {
            params.set('start', filters.startDate);
            params.set('end', filters.endDate);
        }
        if (filters.specialty) params.set('specialty', filters.specialty);
        if (filters.doctorId) params.set('doctorId', filters.doctorId);
        if (filters.recurrence) params.set('recurrence', filters.recurrence);
        const query = params.toString() ? '?' + params.toString() : '';

        Promise.all([
            fetchApi('/referral-analytics/stats' + query).catch(function() { return null; }),
            fetchApi('/referral-analytics/monthly' + query).catch(function() { return null; }),
            fetchApi('/referral-analytics/top-referrers' + query).catch(function() { return null; }),
            fetchApi('/referral-analytics/recent' + query).catch(function() { return null; })
        ])
        .then(function(results) {
            if (results[0]) setStats(results[0].stats);
            if (results[1]) setMonthly(results[1].months || []);
            if (results[2]) setTopReferrers(results[2].referrers || []);
            if (results[3]) setRecentUsers(results[3].users || []);
        })
        .catch(function() {})
        .then(function() { setLoading(false); });
    }, [filters]);

    useEffect(function() { fetchStats(); }, [fetchStats]);

    const handleSort = function(key) {
        if (sortKey === key) {
            setSortAsc(!sortAsc);
        } else {
            setSortKey(key);
            setSortAsc(false);
        }
    };

    const hasActiveFilters = filters.startDate || filters.endDate || filters.specialty || filters.doctorId || filters.recurrence;

    const clearAllFilters = function() {
        setFilters(defaultFilters);
    };

    const sortedReferrers = topReferrers.slice().sort(function(a, b) {
        const valA = a[sortKey] || '';
        const valB = b[sortKey] || '';
        if (typeof valA === 'number') {
            return sortAsc ? valA - valB : valB - valA;
        }
        return sortAsc ? String(valA).localeCompare(String(valB)) : String(valB).localeCompare(String(valA));
    });

    const exportReferrers = function() {
        const data = sortedReferrers.map(function(r) {
            return {
                'Parrain': r.referrerName,
                'Email': r.referrerEmail,
                'Code': r.referralCode || '-',
                'Filleuls inscrits': r.referralCount,
                'Avec consultation': r.withRequest,
                'Taux conversion (%)': r.referralCount > 0 ? Math.round((r.withRequest / r.referralCount) * 100) : 0
            };
        });
        const cols = [
            { wch: 25 },
            { wch: 30 },
            { wch: 12 },
            { wch: 18 },
            { wch: 18 },
            { wch: 20 },
        ];
        exportAsExcel(data, 'parrains_' + new Date().toISOString(), cols);
    };

    const exportRecent = function() {
        const data = recentUsers.map(function(u) {
            return {
                'Filleul': u.name,
                'Email': u.email,
                'Inscrit le': u.createdAt ? dateFormat(u.createdAt, 'dd/mm/yyyy HH:MM') : '-',
                'Consultations': u.requestCount,
                'Parrain': u.referrer ? u.referrer.name : '-',
                'Email parrain': u.referrer ? u.referrer.email : '-',
                'Code parrain': u.referrer ? u.referrer.code || '-' : '-'
            };
        });
        const cols = [
            { wch: 25 },
            { wch: 30 },
            { wch: 18 },
            { wch: 15 },
            { wch: 25 },
            { wch: 30 },
            { wch: 12 },
        ];
        exportAsExcel(data, 'filleuls_' + new Date().toISOString(), cols);
    };

    const maxMonthly = monthly.length > 0 ? Math.max(...monthly.map(function(m) { return m.referredUsers; })) : 1;

    if (loading && !stats) {
        return (
            <div className="ref-container">
                <div className="ref-loading">Chargement des données...</div>
            </div>
        );
    }

    return (
        <div className="ref-container">
            <div className="ref-header">
                <h2 className="ref-title">Parrainage</h2>
            </div>

            {/* Filters */}
            <div className="ref-filters">
                <div className="ref-filters-row">
                    <div className="ref-filter-group">
                        <label className="ref-filter-label">Date début</label>
                        <input
                            className="ref-date-input"
                            type="date"
                            value={filters.startDate}
                            max={filters.endDate || new Date().toISOString().split('T')[0]}
                            onChange={function(e) { updateFilter('startDate', e.target.value); }}
                        />
                    </div>
                    <div className="ref-filter-group">
                        <label className="ref-filter-label">Date fin</label>
                        <input
                            className="ref-date-input"
                            type="date"
                            value={filters.endDate}
                            min={filters.startDate}
                            max={new Date().toISOString().split('T')[0]}
                            onChange={function(e) { updateFilter('endDate', e.target.value); }}
                        />
                    </div>
                    <div className="ref-filter-group">
                        <label className="ref-filter-label">Spécialité</label>
                        <select
                            className="ref-select"
                            value={filters.specialty}
                            onChange={function(e) { updateFilter('specialty', e.target.value); }}
                        >
                            <option value="">Toutes</option>
                            {filterOptions.specialties.map(function(s) {
                                return <option key={s} value={s}>{s}</option>;
                            })}
                        </select>
                    </div>
                    <div className="ref-filter-group">
                        <label className="ref-filter-label">Médecin</label>
                        <select
                            className="ref-select"
                            value={filters.doctorId}
                            onChange={function(e) { updateFilter('doctorId', e.target.value); }}
                        >
                            <option value="">Tous</option>
                            {filterOptions.doctors.map(function(d) {
                                return <option key={d._id} value={d._id}>{d.name}</option>;
                            })}
                        </select>
                    </div>
                    <div className="ref-filter-group">
                        <label className="ref-filter-label">Récurrence</label>
                        <select
                            className="ref-select"
                            value={filters.recurrence}
                            onChange={function(e) { updateFilter('recurrence', e.target.value); }}
                        >
                            <option value="">Tous</option>
                            <option value="recurring">Récurrents (2+ consultations)</option>
                            <option value="single">Non récurrents (1 consultation)</option>
                        </select>
                    </div>
                </div>
                <div className="ref-filters-actions">
                    <button className="ref-btn ref-btn-sm" onClick={fetchStats} disabled={loading}>
                        {loading ? '...' : 'Filtrer'}
                    </button>
                    {hasActiveFilters && (
                        <button className="ref-btn ref-btn-sm ref-btn-ghost" onClick={clearAllFilters}>
                            Effacer les filtres
                        </button>
                    )}
                </div>
            </div>

            {/* KPI Cards */}
            {stats && (
                <div className="ref-kpi-grid">
                    <div className="ref-kpi-card">
                        <div className="ref-kpi-value">{stats.totalUsers.toLocaleString()}</div>
                        <div className="ref-kpi-label">Utilisateurs total</div>
                    </div>
                    <div className="ref-kpi-card">
                        <div className="ref-kpi-value">{stats.usersWithCode.toLocaleString()}</div>
                        <div className="ref-kpi-label">Avec code parrainage</div>
                    </div>
                    <div className="ref-kpi-card ref-kpi-highlight">
                        <div className="ref-kpi-value">{stats.referredUsers.toLocaleString()}</div>
                        <div className="ref-kpi-label">Inscrits via parrainage</div>
                    </div>
                    <div className="ref-kpi-card">
                        <div className="ref-kpi-value">{stats.referredWithRequest.toLocaleString()}</div>
                        <div className="ref-kpi-label">Avec consultation</div>
                    </div>
                    <div className="ref-kpi-card">
                        <div className="ref-kpi-value">{stats.conversionRate}%</div>
                        <div className="ref-kpi-label">Taux de conversion</div>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div className="ref-tabs">
                <button className={'ref-tab' + (activeTab === 'overview' ? ' active' : '')} onClick={function() { setActiveTab('overview'); }}>
                    Évolution mensuelle
                </button>
                <button className={'ref-tab' + (activeTab === 'referrers' ? ' active' : '')} onClick={function() { setActiveTab('referrers'); }}>
                    Top parrains
                </button>
                <button className={'ref-tab' + (activeTab === 'recent' ? ' active' : '')} onClick={function() { setActiveTab('recent'); }}>
                    Inscriptions récentes
                </button>
            </div>

            {/* Monthly Chart */}
            {activeTab === 'overview' && (
                <div className="ref-card">
                    <h3 className="ref-card-title">Inscriptions via parrainage par mois</h3>
                    {monthly.length === 0 ? (
                        <div className="ref-empty">Aucune donnée disponible</div>
                    ) : (
                        <div className="ref-chart">
                            {monthly.map(function(m) {
                                var pct = maxMonthly > 0 ? (m.referredUsers / maxMonthly) * 100 : 0;
                                var pctReq = maxMonthly > 0 ? (m.withRequest / maxMonthly) * 100 : 0;
                                return (
                                    <div className="ref-chart-col" key={m.label}>
                                        <div className="ref-chart-values">
                                            <span className="ref-chart-val">{m.referredUsers}</span>
                                            {m.withRequest > 0 && <span className="ref-chart-val ref-chart-val-sub">{m.withRequest}</span>}
                                        </div>
                                        <div className="ref-chart-bar-wrap">
                                            <div className="ref-chart-bar" style={{ height: pct + '%' }} />
                                            <div className="ref-chart-bar ref-chart-bar-alt" style={{ height: pctReq + '%' }} />
                                        </div>
                                        <div className="ref-chart-label">{m.label}</div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    <div className="ref-chart-legend">
                        <span><i className="ref-legend-dot ref-legend-primary" /> Inscrits via parrainage</span>
                        <span><i className="ref-legend-dot ref-legend-alt" /> Avec consultation</span>
                    </div>
                </div>
            )}

            {/* Top Referrers */}
            {activeTab === 'referrers' && (
                <div className="ref-card">
                    <div className="ref-card-header">
                        <h3 className="ref-card-title">Top parrains</h3>
                        <button className="ref-btn ref-btn-sm" onClick={exportReferrers} disabled={topReferrers.length === 0}>
                            Exporter
                        </button>
                    </div>
                    {topReferrers.length === 0 ? (
                        <div className="ref-empty">Aucun parrain trouvé</div>
                    ) : (
                        <div className="table-responsive">
                            <table className="ref-table">
                                <thead>
                                    <tr>
                                        <th className="sortable" onClick={function() { handleSort('referrerName'); }}>
                                            Parrain {sortKey === 'referrerName' && <i className={'zmdi zmdi-caret-' + (sortAsc ? 'up' : 'down')} />}
                                        </th>
                                        <th>Email</th>
                                        <th>Code</th>
                                        <th className="sortable" onClick={function() { handleSort('referralCount'); }}>
                                            Filleuls {sortKey === 'referralCount' && <i className={'zmdi zmdi-caret-' + (sortAsc ? 'up' : 'down')} />}
                                        </th>
                                        <th className="sortable" onClick={function() { handleSort('withRequest'); }}>
                                            Avec consultation {sortKey === 'withRequest' && <i className={'zmdi zmdi-caret-' + (sortAsc ? 'up' : 'down')} />}
                                        </th>
                                        <th>Conversion</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedReferrers.map(function(r) {
                                        var rate = r.referralCount > 0 ? Math.round((r.withRequest / r.referralCount) * 100) : 0;
                                        return (
                                            <tr key={r._id}>
                                                <td className="ref-cell-name">{r.referrerName}</td>
                                                <td>{r.referrerEmail}</td>
                                                <td><code className="ref-code">{r.referralCode || '-'}</code></td>
                                                <td><strong>{r.referralCount}</strong></td>
                                                <td>{r.withRequest}</td>
                                                <td>
                                                    <span className={'ref-badge' + (rate >= 50 ? ' ref-badge-green' : rate >= 25 ? ' ref-badge-yellow' : ' ref-badge-red')}>
                                                        {rate}%
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Recent Signups */}
            {activeTab === 'recent' && (
                <div className="ref-card">
                    <div className="ref-card-header">
                        <h3 className="ref-card-title">Dernières inscriptions via parrainage</h3>
                        <button className="ref-btn ref-btn-sm" onClick={exportRecent} disabled={recentUsers.length === 0}>
                            Exporter
                        </button>
                    </div>
                    {recentUsers.length === 0 ? (
                        <div className="ref-empty">Aucune inscription récente via parrainage</div>
                    ) : (
                        <div className="table-responsive">
                            <table className="ref-table">
                                <thead>
                                    <tr>
                                        <th>Filleul</th>
                                        <th>Email</th>
                                        <th>Inscrit le</th>
                                        <th>Consultations</th>
                                        <th>Parrainé par</th>
                                        <th>Code</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentUsers.map(function(u) {
                                        return (
                                            <tr key={u._id}>
                                                <td className="ref-cell-name">{u.name}</td>
                                                <td>{u.email}</td>
                                                <td>{u.createdAt ? dateFormat(u.createdAt, 'dd/mm/yyyy HH:MM') : '-'}</td>
                                                <td>
                                                    {u.requestCount > 0 ? (
                                                        <span className="ref-badge ref-badge-green">{u.requestCount}</span>
                                                    ) : (
                                                        <span className="ref-badge ref-badge-red">0</span>
                                                    )}
                                                </td>
                                                <td>{u.referrer ? u.referrer.name : '-'}</td>
                                                <td>{u.referrer ? <code className="ref-code">{u.referrer.code || '-'}</code> : '-'}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ReferralStats;
